import React, { useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { X } from 'lucide-react';
import { ListBuilderItem } from '../ListBuilder';

interface AddItemModalProps {
  title: string;
  availableItems: ListBuilderItem[];
  onConfirm: (selectedIds: number[]) => void;
  onCancel: () => void;
}

const AddItemModal: React.FC<AddItemModalProps> = ({
  title,
  availableItems,
  onConfirm,
  onCancel,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const selectOptions = availableItems.map(item => ({
    value: item.id.toString(),
    label: item.label,
  }));

  const handleChange = (
    selected: MultiValue<{ value: string; label: string }>
  ) => {
    const ids = selected.map(opt => parseInt(opt.value, 10));
    setSelectedIds(ids);
  };

  const handleConfirm = () => {
    if (selectedIds.length > 0) {
      onConfirm(selectedIds);
    }
  };

  return (
    <>
      {/* Modal Overlay */}
      <div style={modalStyles.overlay} onClick={onCancel} />

      {/* Modal Content */}
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            style={modalStyles.closeButton}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, modalStyles.closeButtonHover)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, modalStyles.closeButton)
            }
            aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.fieldContainer}>
            <label style={modalStyles.label}>Select items to add</label>
            <Select
              value={selectOptions.filter(opt =>
                selectedIds.includes(parseInt(opt.value, 10))
              )}
              onChange={handleChange}
              options={selectOptions}
              isMulti
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{
                control: base => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                }),
                menuPortal: base => ({ ...base, zIndex: 10002 }),
              }}
            />
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button
            type="button"
            onClick={onCancel}
            style={modalStyles.cancelButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            style={{
              ...modalStyles.confirmButton,
              ...(selectedIds.length === 0
                ? modalStyles.confirmButtonDisabled
                : {}),
            }}>
            Add Selected ({selectedIds.length})
          </button>
        </div>
      </div>
    </>
  );
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10001,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    zIndex: 10002,
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    padding: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  closeButtonHover: {
    color: '#374151',
    backgroundColor: '#f3f4f6',
    padding: '4px',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  body: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmButtonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
};

export default React.memo(AddItemModal);
